import React from "react";
import axios from "axios";
import SelectInvestor from "../../SelectInvestor/SelectInvestor";
import {executeQuery} from "../../../requests/executeQuery";
import {GET_PROPERTY_OWNERSHIPS, GET_OWNERSHIP_INVESTORS} from "../../../consts/queries";
import {PROPERTY_OWNERSHIP, PROPERTY_OWNERSHIP_INVESTORS, INVESTORS} from "../../../consts/tables";
import {manageData, getData} from "../../../requests/CRUD";
import {Link} from "react-router";
import Input from "../../Input-new/Input";
import {formatPhoneNumber} from "../../../utils";
import OpenInCrmButton from "../../OpenInCrmButton/OpenInCrmButton";
import Checkbox from "../../CheckboxNew";
import DeceasedPill from "../../DeceasedPill/DeceasedPill";

// Add keyframes for spinner animation
if (typeof document !== 'undefined') {
    const style = document.createElement('style');
    style.innerHTML = `
        @keyframes ownership-spinner {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
        }
    `;
    if (!document.querySelector('[data-ownership-styles]')) {
        style.setAttribute('data-ownership-styles', 'true');
        document.head.appendChild(style);
    }
}

/** Empty or whitespace → null so Arc stores NULL, not a default or fabricated date. */
function ownershipDateOrNull(value) {
    if (value == null) return null;
    const s = String(value).trim();
    return s === '' ? null : s;
}

const MailIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
        <polyline points="22,6 12,13 2,6"></polyline>
    </svg>
);

const PhoneIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
    </svg>
);

/** Who to call for a deceased investor, in the same lines the investor's own contact details use. */
const SecondContact = ({investor, color = '#5f6368', spaced = false}) => {
    if (!investor.SECOND_CONTACT_ID || !investor.SECOND_CONTACT_NAME) {
        return null;
    }

    const line = {display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color};

    return (
        <div style={{marginBottom: spaced ? '12px' : 0, paddingTop: '12px', borderTop: '1px solid #e8eaed', display: 'flex', flexDirection: 'column', gap: '6px'}}>
            <span style={{fontSize: '11px', letterSpacing: '0.04em', textTransform: 'uppercase', color}}>2nd Contact</span>
            <Link
                to={`${arc.path.base}investor/${investor.SECOND_CONTACT_ID}`}
                style={{fontSize: '14px', fontWeight: '500', color: '#1a73e8', textDecoration: 'none'}}
            >
                {investor.SECOND_CONTACT_NAME}
            </Link>
            {investor.SECOND_CONTACT_EMAIL && (
                <div style={line}>
                    <MailIcon />
                    <span style={{wordBreak: 'break-all'}}>{investor.SECOND_CONTACT_EMAIL}</span>
                </div>
            )}
            {investor.SECOND_CONTACT_PHONE && (
                <div style={line}>
                    <PhoneIcon />
                    <span>{formatPhoneNumber(investor.SECOND_CONTACT_PHONE)}</span>
                </div>
            )}
        </div>
    );
};

const OwnershipInfo = ({data, propertyId, handleOwnersChange, viewMode, deleted, addNewItem, isNeedOwner, onNeedOwnerChange, isResearched, onResearchedChange, snapshotOwnerships, snapshotInvestors}) => {
    const hasSnapshot = Array.isArray(snapshotOwnerships);
    const [ownerships, setOwnerships] = React.useState([]);
    const [isLoading, setIsLoading] = React.useState(false);
    const [syncingCount, setSyncingCount] = React.useState(0);
    const [selectedInvestorIds, setSelectedInvestorIds] = React.useState('');
    const [startDate, setStartDate] = React.useState('');
    const [endDate, setEndDate] = React.useState('');
    const [error, setError] = React.useState('');
    const [isEditingDates, setIsEditingDates] = React.useState(false)
    const isSyncing = syncingCount > 0;

    const parseDateParts = (dateStr) => {
        if (!dateStr) return null;
        const str = String(dateStr).trim();
        // YYYY-MM-DD or YYYY-MM-DD HH:MM...
        const isoMatch = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (isoMatch) return [Number(isoMatch[1]), Number(isoMatch[2]), Number(isoMatch[3])];
        // MM/DD/YYYY
        const usMatch = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
        if (usMatch) return [Number(usMatch[3]), Number(usMatch[1]), Number(usMatch[2])];
        return null;
    };

    const convertToDateInputFormat = (dateStr) => {
        if (!dateStr) return '';
        const parts = parseDateParts(dateStr);
        if (parts) {
            const [year, month, day] = parts;
            return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        }
        return '';
    };

    const convertFromDateInputFormat = (dateStr) => {
        if (!dateStr) return '';
        const parts = parseDateParts(dateStr);
        if (parts) {
            const [year, month, day] = parts;
            return `${String(month).padStart(2, '0')}/${String(day).padStart(2, '0')}/${year}`;
        }
        return dateStr;
    };

    React.useEffect(() => {
        setSelectedInvestorIds('');
        setStartDate('');
        setEndDate('');
        setError('');
    }, [propertyId]);

    React.useEffect(() => {
        if (hasSnapshot) {
            const seeded = (snapshotOwnerships || []).map((ownership) => {
                const investors = snapshotInvestors && ownership && ownership.ARCIMEDES_ROW_ID
                    ? (snapshotInvestors[ownership.ARCIMEDES_ROW_ID] || [])
                    : [];
                return { ...ownership, investors };
            });
            setOwnerships(seeded);
            setIsLoading(false);
            return;
        }
        if (propertyId) {
            fetchOwnerships();
        } else {
            setOwnerships([]);
        }
    }, [propertyId, hasSnapshot, snapshotOwnerships, snapshotInvestors]);

    const getOwnershipInvestorIds = React.useCallback((ownershipId) => {
        const ownership = ownerships.find((item) => String(item.ARCIMEDES_ROW_ID) === String(ownershipId));
        if (!ownership || !Array.isArray(ownership.investors)) {
            return [];
        }
        return ownership.investors
            .map((investor) => Number(investor.INVESTOR_ID))
            .filter((id) => Number.isFinite(id));
    }, [ownerships]);

    const syncInvestorProperties = React.useCallback(async ({
        addInvestorIds = [],
        removeInvestorIds = [],
        removalKind = 'archive',
    }) => {
        const addIds = (Array.isArray(addInvestorIds) ? addInvestorIds : [addInvestorIds])
            .map((id) => Number(id))
            .filter((id) => Number.isFinite(id));
        const removeIds = (Array.isArray(removeInvestorIds) ? removeInvestorIds : [removeInvestorIds])
            .map((id) => Number(id))
            .filter((id) => Number.isFinite(id));

        if (!propertyId || (!addIds.length && !removeIds.length)) {
            return;
        }

        setSyncingCount((count) => count + 1);
        try {
            const payload = {
                propertyId,
                addInvestorIds: addIds,
                removeInvestorIds: removeIds,
                propertyCategory: data && data.CATEGORY || null,
                propertyType: data && data.TYPE || null,
            };
            if (removeIds.length) {
                payload.removalKind = removalKind;
            }
            await axios.post('/api/crm/investor-properties', payload, { withCredentials: true })
                .then((response) => {
                    if (response && response.data && response.data.product_type_sync) {
                        console.info('CRM product type sync result', response.data.product_type_sync);
                    }
                });
        } catch (err) {
            console.error('CRM investor properties sync failed', err && err.response ? err.response.data : err);
        } finally {
            setSyncingCount((count) => Math.max(0, count - 1));
        }
    }, [propertyId, data && data.CATEGORY, data && data.TYPE]);

    const fetchOwnerships = () => {
        if (!propertyId) return;
        setIsLoading(true);
        const queryParams = {
            PROPERTY_ID: String(propertyId),
            limit: String(9999),
            offset: String(0)
        };

        executeQuery({
            queryName: GET_PROPERTY_OWNERSHIPS,
            options: {
                param: queryParams
            }
        }).then(res => {
            if (res && res.success && Array.isArray(res.data)) {
                // Fetch investors for each ownership
                const ownershipPromises = res.data.map(ownership => 
                    executeQuery({
                        queryName: GET_OWNERSHIP_INVESTORS,
                        options: {
                            param: {
                                PROPERTY_OWNERSHIP_ID: String(ownership.ARCIMEDES_ROW_ID),
                                limit: String(9999),
                                offset: String(0)
                            }
                        }
                    }).then(investorRes => ({
                        ...ownership,
                        investors: investorRes && investorRes.success ? investorRes.data : []
                    }))
                );

                Promise.all(ownershipPromises).then(ownershipsWithInvestors => {
                    setOwnerships(ownershipsWithInvestors);
                    setIsLoading(false);
                });
            } else {
                setIsLoading(false);
            }
        }).catch(err => {
            console.error('Error fetching ownerships:', err);
            setIsLoading(false);
        });
    };
    

    const handleAddOwnership = () => {
        if (!selectedInvestorIds || !propertyId) {
            setError('Please select investors');
            return;
        }

        const sd = ownershipDateOrNull(convertFromDateInputFormat(startDate));
        const ed = ownershipDateOrNull(convertFromDateInputFormat(endDate));

        // Validate dates - only if both are provided
        if (sd && ed) {
            const sParts = parseDateParts(sd);
            const eParts = parseDateParts(ed);
            if (sParts && eParts) {
                const startDateObj = new Date(sParts[0], sParts[1] - 1, sParts[2]);
                const endDateObj = new Date(eParts[0], eParts[1] - 1, eParts[2]);
                if (endDateObj < startDateObj) {
                    setError('End date must be after start date');
                    return;
                }
            }
        }

        setError('');
        setIsLoading(true);

        // Determine whether the new owner is current or archived (historical)
        // from the end date: no end date, or an end date today-or-later, means
        // current.
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        let isCurrent = 1;
        if (ed) {
            const eParts = parseDateParts(ed);
            if (eParts) {
                const endDateObj = new Date(eParts[0], eParts[1] - 1, eParts[2]);
                isCurrent = (endDateObj >= today) ? 1 : 0;
            }
        }

        // Always create a NEW ownership period for this add — never reuse an
        // existing one. Each add is its own period so every owner can be
        // archived/restored independently and a new owner is never merged into
        // another owner's period, current OR archived (547292). Co-owners who
        // truly share a period are added together in a single add via the
        // multi-select above. Omit unset dates so Arc does not substitute
        // "today" for null.
        const row = {
            PROPERTY_ID: Number(propertyId),
            IS_CURRENT: isCurrent
        };
        if (sd !== null) row.START_DATE = sd;
        if (ed !== null) row.END_DATE = ed;

        manageData({
            tableCode: PROPERTY_OWNERSHIP,
            data: [row]
        }).then(ownershipRes => {
            if (ownershipRes && ownershipRes.success && ownershipRes.data && ownershipRes.data.length > 0) {
                attachInvestorsToOwnership(ownershipRes.data[0].ARCIMEDES_ROW_ID, isCurrent === 1);
            } else {
                setError('Failed to create ownership');
                setIsLoading(false);
            }
        }).catch(err => {
            console.error('Error creating ownership:', err);
            setError('Error creating ownership');
            setIsLoading(false);
        });
    };

    const attachInvestorsToOwnership = (ownershipId, isCurrent) => {
        // Convert comma-separated string to array
        let investorIds = [];
        if (typeof selectedInvestorIds === 'string') {
            investorIds = selectedInvestorIds.split(',').filter(id => id && id.trim());
        } else if (Array.isArray(selectedInvestorIds)) {
            investorIds = selectedInvestorIds.filter(id => id);
        } else if (selectedInvestorIds) {
            investorIds = [selectedInvestorIds];
        }

        if (investorIds.length === 0) {
            setIsLoading(false);
            return;
        }

        const pivotRecords = investorIds.map(investorId => ({
            PROPERTY_OWNERSHIP_ID: Number(ownershipId),
            INVESTOR_ID: Number(investorId)
        }));

        manageData({
            tableCode: PROPERTY_OWNERSHIP_INVESTORS,
            data: pivotRecords
        }).then(res => {
            if (res && res.success) {
                if (isCurrent) {
                    syncInvestorProperties({ addInvestorIds: investorIds });
                } else {
                    // A past owner goes to the lead's Previous Properties, unless
                    // they own it now too: Kore lists it once, as current.
                    const ownersNow = ownerships
                        .filter((ownership) => isCurrentOwnership(ownership))
                        .flatMap((ownership) => getOwnershipInvestorIds(ownership.ARCIMEDES_ROW_ID));

                    syncInvestorProperties({
                        removeInvestorIds: investorIds.filter((id) => ownersNow.indexOf(Number(id)) === -1),
                        removalKind: 'archive',
                    });
                }
                handleOwnersChange();
                // Reset form
                setSelectedInvestorIds('');
                setStartDate('');
                setEndDate('');
                setError('');
                // Refresh ownerships
                fetchOwnerships();
            } else {
                setError('Failed to attach investors');
                setIsLoading(false);
            }
        }).catch(err => {
            console.error('Error attaching investors:', err);
            setError('Error attaching investors');
            setIsLoading(false);
        });
    };

    const handleRemoveInvestor = (pivotId, investorId) => {
        if (!pivotId) return;

        manageData({
            tableCode: PROPERTY_OWNERSHIP_INVESTORS,
            data: [{
                ARCIMEDES_ROW_ID: pivotId,
                DELETED: 1
            }]
        }).then(() => {
            if (investorId) {
                syncInvestorProperties({ removeInvestorIds: [investorId], removalKind: 'detach' });
            }
            handleOwnersChange();
            fetchOwnerships();
        }).catch(err => {
            console.error('Error removing investor:', err);
        });
    };

    const handleToggleCurrent = (ownership) => {
        if (!ownership.ARCIMEDES_ROW_ID) return;

        // Handle both string "1" and number 1 for IS_CURRENT
        const currentStatus = (ownership.IS_CURRENT === 1 || ownership.IS_CURRENT === "1") ? 1 : 0;
        const newStatus = currentStatus === 1 ? 0 : 1;

        const investorIds = getOwnershipInvestorIds(ownership.ARCIMEDES_ROW_ID);
        manageData({
            tableCode: PROPERTY_OWNERSHIP,
            data: [{
                ARCIMEDES_ROW_ID: ownership.ARCIMEDES_ROW_ID,
                PROPERTY_ID: ownership.PROPERTY_ID,
                START_DATE: ownershipDateOrNull(ownership.START_DATE),
                END_DATE: ownershipDateOrNull(ownership.END_DATE),
                IS_CURRENT: newStatus
            }]
        }).then((response) => {
            if (newStatus === 1) {
                syncInvestorProperties({ addInvestorIds: investorIds });
            } else {
                syncInvestorProperties({ removeInvestorIds: investorIds });
            }
            handleOwnersChange();
            fetchOwnerships();
        }).catch(err => {
            console.error('Error toggling current status:', err);
        });
    };

    const handleRemoveOwnership = (ownershipId) => {
        if (!ownershipId) return;

        const investorIds = getOwnershipInvestorIds(ownershipId);
        manageData({
            tableCode: PROPERTY_OWNERSHIP,
            data: [{
                ARCIMEDES_ROW_ID: ownershipId,
                DELETED: 1
            }]
        }).then(() => {
            syncInvestorProperties({ removeInvestorIds: investorIds, removalKind: 'detach' });
            handleOwnersChange();
            fetchOwnerships();
        }).catch(err => {
            console.error('Error removing ownership:', err);
        });
    };

    const getInvestorDisplayName = (investor) => {
        return investor.NAME || 'Unnamed Investor';
    };

    const formatDate = (dateStr, whenMissing) => {
        if (!dateStr) {
            return whenMissing;
        }
        const parts = parseDateParts(dateStr);
        if (!parts) return dateStr;
        const [year, month, day] = parts;
        const date = new Date(year, month - 1, day);
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    };

    const isCurrentOwnership = (ownership) => {
        // Only check IS_CURRENT flag - ownership is current/historical based solely on this field
        // IS_CURRENT can be number 1/0 or string "1"/"0"
        const isCurrent = ownership.IS_CURRENT === 1 || ownership.IS_CURRENT === "1";
        return isCurrent;
    };

    const onEditDateChange = (e, id) => {
        const {value, name} = e.target;
        setOwnerships(prev =>
            prev.map(ownership =>
                ownership.ARCIMEDES_ROW_ID === id
                    ? { ...ownership, [name]: value }
                    : ownership
            )
        );
    }

    const editSubmit = (ownership) => {
        manageData({
            tableCode: PROPERTY_OWNERSHIP,
            data: [{
                ARCIMEDES_ROW_ID: ownership.ARCIMEDES_ROW_ID,
                PROPERTY_ID: ownership.PROPERTY_ID,
                START_DATE: ownershipDateOrNull(convertFromDateInputFormat(ownership.START_DATE)),
                END_DATE: ownershipDateOrNull(convertFromDateInputFormat(ownership.END_DATE)),
                IS_CURRENT: (ownership.IS_CURRENT === 1 || ownership.IS_CURRENT === "1") ? 1 : 0
            }]
        }).then(() => {
            setIsEditingDates("");
            fetchOwnerships();
        });
    };

    const currentOwnerships = ownerships.filter(o => isCurrentOwnership(o));
    const historicalOwnerships = ownerships.filter(o => !isCurrentOwnership(o));

    // Get list of all attached investor IDs to exclude from dropdown
    const attachedInvestorIds = ownerships.flatMap(o => 
        o.investors ? o.investors.map(inv => String(inv.INVESTOR_ID)) : []
    );

    return (
        <div className="Owners-info" style={{padding: '24px', backgroundColor: '#fafbfc'}}>
            {isSyncing && (
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '8px 12px',
                    marginBottom: '16px',
                    backgroundColor: '#fffbe6',
                    border: '1px solid #ffe58f',
                    borderRadius: '8px',
                    color: '#7a4f01',
                    fontSize: '13px',
                    fontWeight: '500'
                }}>
                    <div style={{
                        width: '14px',
                        height: '14px',
                        border: '2px solid #ffe58f',
                        borderTop: '2px solid #d48806',
                        borderRadius: '50%',
                        animation: 'ownership-spinner 1s linear infinite'
                    }}></div>
                    Syncing with CRM...
                </div>
            )}
            <div style={{marginBottom: '16px', display: 'flex', gap: '24px'}} className="no-margin-checkbox">
                <Checkbox
                    text="NEED OWNER"
                    type={viewMode ? "uncheckable" : "checkable"}
                    isSelected={isNeedOwner}
                    handleClick={() => onNeedOwnerChange(!isNeedOwner)}
                    name="NEED_OWNER"
                />
                <Checkbox
                    text="RESEARCHED"
                    type={viewMode ? "uncheckable" : "checkable"}
                    isSelected={isResearched}
                    handleClick={() => onResearchedChange(!isResearched)}
                    name="RESEARCHED"
                />
            </div>
            {!viewMode && propertyId && (
                <div style={{
                    backgroundColor: '#ffffff',
                    borderRadius: '12px',
                    padding: '24px',
                    marginBottom: '32px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                    border: '1px solid #e8eaed'
                }}>
                    <h3 style={{
                        margin: '0 0 20px 0',
                        fontSize: '16px',
                        fontWeight: '600',
                        color: '#202124',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                    }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                            <line x1="16" y1="2" x2="16" y2="6"></line>
                            <line x1="8" y1="2" x2="8" y2="6"></line>
                            <line x1="3" y1="10" x2="21" y2="10"></line>
                        </svg>
                        Add New Ownership Period
                    </h3>
                    
                    <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr 2fr', gap: '16px', marginBottom: '16px'}}>
                        <div className="input-container default">
                            <label className="input-label default" style={{fontWeight: '500', color: '#5f6368', marginBottom: '8px'}}>Start Date</label>
                            <div className="input-wrapper">
                                <input
                                    className="input default"
                                    type="date"
                                    name="START_DATE"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    disabled={deleted}
                                    style={{
                                        borderRadius: '8px',
                                        border: '1px solid #dadce0',
                                        padding: '10px 12px',
                                        fontSize: '14px'
                                    }}
                                />
                            </div>
                        </div>
                        <div className="input-container default">
                            <label className="input-label default" style={{fontWeight: '500', color: '#5f6368', marginBottom: '8px'}}>End Date</label>
                            <div className="input-wrapper">
                                <input
                                    className="input default"
                                    type="date"
                                    name="END_DATE"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    disabled={deleted}
                                    style={{
                                        borderRadius: '8px',
                                        border: '1px solid #dadce0',
                                        padding: '10px 12px',
                                        fontSize: '14px'
                                    }}
                                />
                            </div>
                        </div>
                        <SelectInvestor
                            key={propertyId}
                            dropdownType="multiple"
                            value={selectedInvestorIds}
                            valueKey="INVESTOR_IDS"
                            label="Select Investors *"
                            data={{}}
                            handleParentChange={(data, optionsType, value, valueKey) => {
                                setSelectedInvestorIds(data[valueKey] || '');
                            }}
                            withAddNew
                            addNewItem={addNewItem}
                            viewMode={false}
                            deleted={deleted}
                            excludeInvestorIds={[]}
                        />
                    </div>
                    
                    {error && (
                        <div style={{
                            backgroundColor: '#fef7f7',
                            border: '1px solid #f5c6cb',
                            borderRadius: '8px',
                            padding: '12px 16px',
                            marginBottom: '16px',
                            color: '#d32f2f',
                            fontSize: '14px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                            </svg>
                            {error}
                        </div>
                    )}

                    <div style={{display: 'flex', gap: '12px', justifyContent: 'flex-end'}}>
                        <button
                            type="button"
                            onClick={addNewItem}
                            style={{
                                padding: '10px 20px',
                                borderRadius: '8px',
                                border: '1px solid #dadce0',
                                backgroundColor: '#ffffff',
                                color: '#5f6368',
                                fontSize: '14px',
                                fontWeight: '500',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px'
                            }}
                            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                            onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#ffffff'}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                                <circle cx="8.5" cy="7" r="4"></circle>
                                <line x1="20" y1="8" x2="20" y2="14"></line>
                                <line x1="23" y1="11" x2="17" y2="11"></line>
                            </svg>
                            Create New Investor
                        </button>
                        <button
                            type="button"
                            onClick={handleAddOwnership}
                            disabled={!selectedInvestorIds}
                            style={{
                                padding: '10px 24px',
                                borderRadius: '8px',
                                border: 'none',
                                backgroundColor: (!selectedInvestorIds) ? '#e0e0e0' : '#1a73e8',
                                color: '#ffffff',
                                fontSize: '14px',
                                fontWeight: '500',
                                cursor: (!selectedInvestorIds) ? 'not-allowed' : 'pointer',
                                transition: 'all 0.2s',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                boxShadow: (!selectedInvestorIds) ? 'none' : '0 1px 3px rgba(26,115,232,0.3)'
                            }}
                            onMouseOver={(e) => {
                                if (!(!selectedInvestorIds)) {
                                    e.currentTarget.style.backgroundColor = '#1557b0';
                                    e.currentTarget.style.boxShadow = '0 2px 6px rgba(26,115,232,0.4)';
                                }
                            }}
                            onMouseOut={(e) => {
                                if (!(!selectedInvestorIds)) {
                                    e.currentTarget.style.backgroundColor = '#1a73e8';
                                    e.currentTarget.style.boxShadow = '0 1px 3px rgba(26,115,232,0.3)';
                                }
                            }}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="12" y1="5" x2="12" y2="19"></line>
                                <line x1="5" y1="12" x2="19" y2="12"></line>
                            </svg>
                            Add Ownership Period
                        </button>
                    </div>
                </div>
            )}

            {isLoading && (
                <div style={{
                    textAlign: 'center',
                    padding: '60px 20px',
                    backgroundColor: '#ffffff',
                    borderRadius: '12px',
                    border: '1px solid #e8eaed'
                }}>
                    <div style={{
                        width: '40px',
                        height: '40px',
                        border: '3px solid #f3f3f3',
                        borderTop: '3px solid #1a73e8',
                        borderRadius: '50%',
                        animation: 'ownership-spinner 1s linear infinite',
                        margin: '0 auto 16px'
                    }}></div>
                    <p style={{color: '#5f6368', fontSize: '14px', margin: 0}}>Loading ownership periods...</p>
                </div>
            )}

            {!isLoading && ownerships.length === 0 && (
                <div style={{
                    textAlign: 'center',
                    padding: '60px 20px',
                    backgroundColor: '#ffffff',
                    borderRadius: '12px',
                    border: '2px dashed #dadce0'
                }}>
                    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#dadce0" strokeWidth="1.5" style={{margin: '0 auto 16px'}}>
                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                        <circle cx="9" cy="7" r="4"></circle>
                        <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                        <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                    </svg>
                    <h4 style={{margin: '0 0 8px 0', fontSize: '16px', fontWeight: '600', color: '#202124'}}>
                        No Ownership Periods Yet
                    </h4>
                    <p style={{color: '#5f6368', fontSize: '14px', margin: 0}}>
                        Add investors with start and end dates to create ownership periods
                    </p>
                </div>
            )}

            {!isLoading && currentOwnerships.length > 0 && (
                <div style={{marginBottom: '32px'}}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: '16px'
                    }}>
                        <h3 style={{
                            margin: 0,
                            fontSize: '18px',
                            fontWeight: '600',
                            color: '#202124',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            marginRight: '20px'
                        }}>
                            <span style={{
                                width: '8px',
                                height: '8px',
                                borderRadius: '50%',
                                backgroundColor: '#34a853',
                                boxShadow: '0 0 0 3px rgba(52,168,83,0.2)'
                            }}></span>
                            Current Ownership Periods
                        </h3>
                        <span style={{
                            backgroundColor: '#e8f5e9',
                            color: '#1e8e3e',
                            padding: '4px 12px',
                            borderRadius: '12px',
                            fontSize: '13px',
                            fontWeight: '600',
                            marginLeft: 'auto'
                        }}>
                            {currentOwnerships.length} Active
                        </span>
                    </div>
                    
                    <div style={{display: 'flex', flexDirection: 'column', gap: '16px'}}>
                        {currentOwnerships.map((ownership) =>
                        (
                            <div key={ownership.ARCIMEDES_ROW_ID} style={{
                                backgroundColor: '#ffffff',
                                borderRadius: '12px',
                                border: '1px solid #e8eaed',
                                overflow: 'hidden',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                                transition: 'all 0.2s'
                            }}>
                                <div style={{
                                    backgroundColor: '#f8f9fa',
                                    padding: '16px 20px',
                                    borderBottom: '1px solid #e8eaed',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center'
                                }}>
                                    <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#34a853" strokeWidth="2">
                                            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                                            <line x1="16" y1="2" x2="16" y2="6"></line>
                                            <line x1="8" y1="2" x2="8" y2="6"></line>
                                            <line x1="3" y1="10" x2="21" y2="10"></line>
                                        </svg>
                                        <div style={{display: "flex", alignItems: "center"}}>
                                            <div>
                                                {(!viewMode && isEditingDates == ownership.ARCIMEDES_ROW_ID) ? 
                                                    <div className="input-container default" style={{marginBottom: 0}}>
                                                        <div className="input-wrapper" style={{height: "37px"}}>
                                                            <input
                                                                className="input default"
                                                                type="date"
                                                                name="START_DATE"
                                                                onChange={(e) => onEditDateChange(e, ownership.ARCIMEDES_ROW_ID)}
                                                                value={convertToDateInputFormat(ownership.START_DATE)}
                                                                required
                                                                style={{
                                                                    borderRadius: '8px',
                                                                    border: '1px solid #dadce0',
                                                                    padding: '5px 12px',
                                                                    fontSize: '14px'
                                                                }}
                                                            />
                                                        </div>
                                                    </div>
                                                    :
                                                    <span style={{fontSize: '15px', fontWeight: '600', color: '#202124'}}>
                                                        {formatDate(ownership.START_DATE, 'Unknown')}
                                                    </span>
                                                }
                                            </div>
                                            <span style={{display: "block", margin: "0 10px"}}>→</span>
                                            <div>
                                                {(!viewMode && isEditingDates == ownership.ARCIMEDES_ROW_ID) ?
                                                    <div className="input-container default" style={{marginBottom: 0}}>
                                                        <div className="input-wrapper" style={{height: "37px"}}>
                                                            <input
                                                                className="input default"
                                                                type="date"
                                                                name="END_DATE"
                                                                onChange={(e) => onEditDateChange(e, ownership.ARCIMEDES_ROW_ID)}
                                                                value={convertToDateInputFormat(ownership.END_DATE)}
                                                                required
                                                                style={{
                                                                    borderRadius: '8px',
                                                                    border: '1px solid #dadce0',
                                                                    padding: '5px 12px',
                                                                    fontSize: '14px'
                                                                }}
                                                            />
                                                        </div>
                                                    </div>
                                                    :
                                                    <span style={{fontSize: '15px', fontWeight: '600', color: '#202124'}}>
                                                        {formatDate(ownership.END_DATE, 'Current')}
                                                    </span>
                                                }
                                            </div>
                                            {(!viewMode && isEditingDates == ownership.ARCIMEDES_ROW_ID) && 
                                                <button 
                                                    type="button" 
                                                    onClick={() => setIsEditingDates("")}
                                                    style={{
                                                        padding: '10px 14px',
                                                        borderRadius: '6px',
                                                        border: '1px solid #dadce0',
                                                        backgroundColor: '#ffffff',
                                                        color: '#5f6368',
                                                    }}
                                                >
                                                    Cancel
                                                </button>
                                            }
                                            {(!viewMode && isEditingDates == ownership.ARCIMEDES_ROW_ID) && <button type="button" onClick={() => editSubmit(ownership)}>Save</button>}
                                        </div>
                                    </div>
                                    {!viewMode && (
                                        <div style={{display: 'flex', gap: '8px', alignItems: "center"}}>
                                            <button
                                                type="button"
                                                onClick={() => handleToggleCurrent(ownership)}
                                                title="Move to history"
                                                style={{
                                                    padding: '6px 14px',
                                                    borderRadius: '6px',
                                                    border: '1px solid #dadce0',
                                                    backgroundColor: '#ffffff',
                                                    color: '#5f6368',
                                                    fontSize: '13px',
                                                    fontWeight: '500',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '4px'
                                                }}
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" width="15px" height="15px" viewBox="0 0 24 24" fill="none">
                                                    <path d="M20.5 7V13C20.5 16.7712 20.5 18.6569 19.3284 19.8284C18.1569 21 16.2712 21 12.5 21H11.5M3.5 7V13C3.5 16.7712 3.5 18.6569 4.67157 19.8284C5.37634 20.5332 6.3395 20.814 7.81608 20.9259" stroke="#000" strokeWidth="1.5" stroke-linecap="round"/>
                                                    <path d="M12 3H4C3.05719 3 2.58579 3 2.29289 3.29289C2 3.58579 2 4.05719 2 5C2 5.94281 2 6.41421 2.29289 6.70711C2.58579 7 3.05719 7 4 7H20C20.9428 7 21.4142 7 21.7071 6.70711C22 6.41421 22 5.94281 22 5C22 4.05719 22 3.58579 21.7071 3.29289C21.4142 3 20.9428 3 20 3H16" stroke="#000" strokeWidth="1.5" stroke-linecap="round"/>
                                                    <path d="M12 7L12 16M12 16L15 12.6667M12 16L9 12.6667" stroke="#000" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                                </svg>
                                                Archive
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setIsEditingDates(ownership.ARCIMEDES_ROW_ID)}
                                                title="Edit Dates"
                                                style={{
                                                    padding: '6px 14px',
                                                    borderRadius: '6px',
                                                    border: '1px solid #dadce0',
                                                    backgroundColor: '#ffffff',
                                                    color: '#5f6368',
                                                    fontSize: '13px',
                                                    fontWeight: '500',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '4px'
                                                }}
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" width="15px" height="15px" viewBox="0 0 24 24" fill="none">
                                                    <path d="M21.2799 6.40005L11.7399 15.94C10.7899 16.89 7.96987 17.33 7.33987 16.7C6.70987 16.07 7.13987 13.25 8.08987 12.3L17.6399 2.75002C17.8754 2.49308 18.1605 2.28654 18.4781 2.14284C18.7956 1.99914 19.139 1.92124 19.4875 1.9139C19.8359 1.90657 20.1823 1.96991 20.5056 2.10012C20.8289 2.23033 21.1225 2.42473 21.3686 2.67153C21.6147 2.91833 21.8083 3.21243 21.9376 3.53609C22.0669 3.85976 22.1294 4.20626 22.1211 4.55471C22.1128 4.90316 22.0339 5.24635 21.8894 5.5635C21.7448 5.88065 21.5375 6.16524 21.2799 6.40005V6.40005Z" 
                                                        stroke="#000000" 
                                                        stroke-width="1.5" 
                                                        stroke-linecap="round" 
                                                        troke-linejoin="round"
                                                    />
                                                    <path d="M11 4H6C4.93913 4 3.92178 4.42142 3.17163 5.17157C2.42149 5.92172 2 6.93913 2 8V18C2 19.0609 2.42149 20.0783 3.17163 20.8284C3.92178 21.5786 4.93913 22 6 22H17C19.21 22 20 20.2 20 18V13" stroke="#000000" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                                                </svg>
                                                Edit
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveOwnership(ownership.ARCIMEDES_ROW_ID)}
                                                title="Remove ownership period"
                                                style={{
                                                    padding: '6px 14px',
                                                    borderRadius: '6px',
                                                    border: '1px solid #fce8e6',
                                                    backgroundColor: '#fce8e6',
                                                    color: '#d93025',
                                                    fontSize: '13px',
                                                    fontWeight: '500',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '4px'
                                                }}
                                            >
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <polyline points="3 6 5 6 21 6"></polyline>
                                                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                                    <line x1="10" y1="11" x2="10" y2="17"></line>
                                                    <line x1="14" y1="11" x2="14" y2="17"></line>
                                                </svg>
                                                Remove
                                            </button>
                                        </div>
                                    )}
                                </div>
                                
                                <div style={{padding: '20px', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px'}}>
                                    {ownership.investors && ownership.investors.map((investor) => (
                                        <div key={investor.ARCIMEDES_ROW_ID} style={{
                                            backgroundColor: '#f8f9fa',
                                            borderRadius: '10px',
                                            padding: '16px',
                                            border: '1px solid #e8eaed',
                                            transition: 'all 0.2s'
                                        }}>
                                            <div style={{marginBottom: '12px', display: 'flex', flexDirection: 'column', gap: '8px'}}>
                                                <Link 
                                                    to={`${arc.path.base}investor/${investor.INVESTOR_ID}`}
                                                    style={{
                                                        fontSize: '15px',
                                                        fontWeight: '600',
                                                        color: '#1a73e8',
                                                        textDecoration: 'none',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '6px'
                                                    }}
                                                >
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                                                        <circle cx="12" cy="7" r="4"></circle>
                                                    </svg>
                                                    {getInvestorDisplayName(investor)}
                                                    <DeceasedPill investor={investor} />
                                                </Link>

                                                {investor.LEAD_ID && (
                                                    <OpenInCrmButton
                                                        leadId={investor.LEAD_ID}
                                                        entityType="lead"
                                                        variant="compact"
                                                        style={{width: 'fit-content'}}
                                                    />
                                                )}
                                            </div>
                                            
                                            {(investor.EMAIL || investor.PHONE) && (
                                                <div style={{marginBottom: '12px', display: 'flex', flexDirection: 'column', gap: '6px'}}>
                                                    {investor.EMAIL && (
                                                        <div style={{display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#5f6368'}}>
                                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                                                                <polyline points="22,6 12,13 2,6"></polyline>
                                                            </svg>
                                                            <span style={{wordBreak: 'break-all'}}>{investor.EMAIL}</span>
                                                        </div>
                                                    )}
                                                    {investor.PHONE && (
                                                        <div style={{display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#5f6368'}}>
                                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                                                            </svg>
                                                            <span>{formatPhoneNumber(investor.PHONE)}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                            
                                            <SecondContact investor={investor} spaced />

                                            {!viewMode && (
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveInvestor(investor.ARCIMEDES_ROW_ID, investor.INVESTOR_ID)}
                                                    title="Remove investor from this period"
                                                    style={{
                                                        width: '100%',
                                                        padding: '8px',
                                                        borderRadius: '6px',
                                                        border: '1px solid #fce8e6',
                                                        backgroundColor: '#ffffff',
                                                        color: '#d93025',
                                                        fontSize: '13px',
                                                        fontWeight: '500',
                                                        cursor: 'pointer',
                                                        transition: 'all 0.2s',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        gap: '6px'
                                                    }}
                                                >
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <line x1="18" y1="6" x2="6" y2="18"></line>
                                                        <line x1="6" y1="6" x2="18" y2="18"></line>
                                                    </svg>
                                                    Remove from Period
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {!isLoading && historicalOwnerships.length > 0 && (
                <div>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: '16px'
                    }}>
                        <h3 style={{
                            margin: 0,
                            fontSize: '18px',
                            fontWeight: '600',
                            color: '#202124',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px'
                        }}>
                            <span style={{
                                width: '8px',
                                height: '8px',
                                borderRadius: '50%',
                                backgroundColor: '#9aa0a6',
                                boxShadow: '0 0 0 3px rgba(154,160,166,0.2)'
                            }}></span>
                            Historical Ownership Periods
                        </h3>
                        <span style={{
                            backgroundColor: '#f1f3f4',
                            color: '#5f6368',
                            padding: '4px 12px',
                            borderRadius: '12px',
                            fontSize: '13px',
                            fontWeight: '600'
                        }}>
                            {historicalOwnerships.length} Past
                        </span>
                    </div>
                    
                    <div style={{display: 'flex', flexDirection: 'column', gap: '16px'}}>
                        {historicalOwnerships.map((ownership) => (
                            <div key={ownership.ARCIMEDES_ROW_ID} style={{
                                backgroundColor: '#ffffff',
                                borderRadius: '12px',
                                border: '1px solid #e8eaed',
                                overflow: 'hidden',
                                opacity: 0.85,
                                transition: 'all 0.2s'
                            }}>
                                <div style={{
                                    backgroundColor: '#f8f9fa',
                                    padding: '16px 20px',
                                    borderBottom: '1px solid #e8eaed',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center'
                                }}>
                                    <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#9aa0a6" strokeWidth="2">
                                            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                                            <line x1="16" y1="2" x2="16" y2="6"></line>
                                            <line x1="8" y1="2" x2="8" y2="6"></line>
                                            <line x1="3" y1="10" x2="21" y2="10"></line>
                                        </svg>
                                        <div>
                                            <span style={{fontSize: '15px', fontWeight: '600', color: '#5f6368'}}>
                                                {formatDate(ownership.START_DATE, 'Unknown')} → {formatDate(ownership.END_DATE, 'Unknown')}
                                            </span>
                                            <span style={{
                                                marginLeft: '10px',
                                                fontSize: '12px',
                                                padding: '2px 8px',
                                                borderRadius: '4px',
                                                backgroundColor: '#f1f3f4',
                                                color: '#5f6368',
                                                fontWeight: '500'
                                            }}>
                                                PAST
                                            </span>
                                        </div>
                                    </div>
                                    {!viewMode && (
                                        <div style={{display: 'flex', gap: '8px'}}>
                                            <button
                                                type="button"
                                                onClick={() => handleToggleCurrent(ownership)}
                                                title="Restore as current"
                                                style={{
                                                    padding: '6px 14px',
                                                    borderRadius: '6px',
                                                    border: '1px solid #e8f5e9',
                                                    backgroundColor: '#e8f5e9',
                                                    color: '#1e8e3e',
                                                    fontSize: '13px',
                                                    fontWeight: '500',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '4px'
                                                }}
                                            >
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <polyline points="23 4 23 10 17 10"></polyline>
                                                    <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path>
                                                </svg>
                                                Restore
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveOwnership(ownership.ARCIMEDES_ROW_ID)}
                                                title="Remove ownership period"
                                                style={{
                                                    padding: '6px 14px',
                                                    borderRadius: '6px',
                                                    border: '1px solid #fce8e6',
                                                    backgroundColor: '#fce8e6',
                                                    color: '#d93025',
                                                    fontSize: '13px',
                                                    fontWeight: '500',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '4px'
                                                }}
                                            >
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <polyline points="3 6 5 6 21 6"></polyline>
                                                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                                    <line x1="10" y1="11" x2="10" y2="17"></line>
                                                    <line x1="14" y1="11" x2="14" y2="17"></line>
                                                </svg>
                                                Remove
                                            </button>
                                        </div>
                                    )}
                                </div>
                                
                                <div style={{padding: '20px', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px'}}>
                                    {ownership.investors && ownership.investors.map((investor) => (
                                        <div key={investor.ARCIMEDES_ROW_ID} style={{
                                            backgroundColor: '#f8f9fa',
                                            borderRadius: '10px',
                                            padding: '16px',
                                            border: '1px solid #e8eaed'
                                        }}>
                                            <div style={{marginBottom: '12px', display: 'flex', flexDirection: 'column', gap: '8px'}}>
                                                <Link 
                                                    to={`${arc.path.base}investor/${investor.INVESTOR_ID}`}
                                                    style={{
                                                        fontSize: '15px',
                                                        fontWeight: '600',
                                                        color: '#5f6368',
                                                        textDecoration: 'none',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '6px'
                                                    }}
                                                >
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                                                        <circle cx="12" cy="7" r="4"></circle>
                                                    </svg>
                                                    {getInvestorDisplayName(investor)}
                                                    <DeceasedPill investor={investor} />
                                                </Link>

                                                {investor.LEAD_ID && (
                                                    <OpenInCrmButton
                                                        leadId={investor.LEAD_ID}
                                                        entityType="lead"
                                                        variant="compact"
                                                        style={{width: 'fit-content'}}
                                                    />
                                                )}
                                            </div>
                                            
                                            {(investor.EMAIL || investor.PHONE) && (
                                                <div style={{display: 'flex', flexDirection: 'column', gap: '6px'}}>
                                                    {investor.EMAIL && (
                                                        <div style={{display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#80868b'}}>
                                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                                                                <polyline points="22,6 12,13 2,6"></polyline>
                                                            </svg>
                                                            <span style={{wordBreak: 'break-all'}}>{investor.EMAIL}</span>
                                                        </div>
                                                    )}
                                                    {investor.PHONE && (
                                                        <div style={{display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#80868b'}}>
                                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                                                            </svg>
                                                            <span>{formatPhoneNumber(investor.PHONE)}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                            <SecondContact investor={investor} color="#80868b" />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default OwnershipInfo;
